import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useTheme } from "@mui/material/styles"
import { makeStyles } from "tss-react/mui"
import classNames from "classnames"

import DashboardLayout from "components/LayoutContainers/DashboardLayout"
import SelectLanguage from "components/SelectLanguage/SelectLaguage"
import MDButton from "components/MDButton"
import useTestParams from "hooks/useTestParams.hook"
import { useMaterialUIController } from "context"
import { getTestTypeLocaleKey } from "utils/testType.utils"
import brandLogo from "assets/images/logos/HeaderLogo.svg"
import { styles } from "./LandingPage.styles"
import { detectHmo } from "utils/hmoDetection"
import { HMO } from "constants/hmo"
import { buildSessionId, DEFAULT_SESSION_ID_PREFIX } from "utils/sessionId.util"

type Tenant = {
  id: string
  label: string
  sessionIdPrefix: string
  testType: string
}

// Add more tenants here to render additional navigation buttons
const TENANTS: Array<Tenant> = [
  {
    id: "leumit",
    label: 'LandingPage:leumit',
    sessionIdPrefix: DEFAULT_SESSION_ID_PREFIX,
    testType: "fob",
  },
]

const LandingPage: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const [{ direction }] = useMaterialUIController()
  const useStyles = makeStyles()(() => styles(theme))
  const { classes } = useStyles()
  const isLeumit = detectHmo() === HMO.LEUMIT

  const { testType } = useTestParams()
  const testTypeLocaleKey = getTestTypeLocaleKey(testType)

  // Relative path so it resolves on any environment / tenant subdomain
  const handleTenantNavigate = (tenant: Tenant): void => {
    const searchParams = new URLSearchParams({
      testtype: tenant.testType,
      sessionid: buildSessionId(tenant.sessionIdPrefix),
    })
    if (isLeumit) {
      navigate(`/mainpage?${searchParams.toString()}`)
      return
    }
    // redirect to the leumit mainpage url
    window.location.href=`https://leumit.labontimeapp.com/mainpage?${searchParams.toString()}`
  }

  return (
    <DashboardLayout>
      <div className={classes.container} data-test-id="landing-page">
        {isLeumit ? null : <div className={classes.headerRow}>
          <img
            src={brandLogo}
            alt={t("common:brandName")}
            className={classes.logo}
          />
          <SelectLanguage />
        </div>}

        <div className={classes.bodyContainer}>
          <div
            className={classNames(
              direction === "ltr" ? classes.bodyContentLTR : classes.bodyContentRTL
            )}
          >
            <h1 className={classes.title} data-test-id="landing-page-title">
              {t(`${testTypeLocaleKey}:LandingPage:title`)}
            </h1>
            <p className={classes.description} data-test-id="landing-page-description">
              {t(`${testTypeLocaleKey}:LandingPage:description`)}
            </p>
            <div className={classes.tenantButtons}>
              {TENANTS.map((tenant) => (
                <MDButton
                  key={tenant.id}
                  className={classes.startButton}
                  color="primary"
                  variant="contained"
                  size="large"
                  onClick={() => handleTenantNavigate(tenant)}
                  data-test-id={`landing-page-${tenant.id}-button`}
                  aria-label={tenant.label}
                >
                  {t(`${testTypeLocaleKey}:${tenant.label}`)}
                </MDButton>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default LandingPage
